import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A windowed list, written by hand rather than pulled in as a dependency.
 *
 * ARCHITECTURE.md asks the entry list to virtualise above 200 rows. Below that
 * it renders everything, because windowing a short list only adds ways to be
 * wrong. Above it, only the rows near the viewport exist in the DOM, with a
 * spacer above and below standing in for the rest.
 *
 * Rows here are whole days, not entries. A day is a natural unit — its sticky
 * header and its entries stay in one containing block, which is what makes
 * `position: sticky` behave — and 6,000 entries over seven years is at most a
 * couple of thousand days.
 *
 * Heights are measured rather than assumed, because a day holding one arterial
 * line and a day holding an admission with four procedures are nothing like the
 * same size. Until a row has been rendered once, its estimate is used.
 */

type VirtualListProps<T> = {
  items: readonly T[];
  keyOf: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  /** Starting guess for an unmeasured row, in pixels. */
  estimateHeight?: number;
  /** Rows rendered beyond the viewport, so scrolling does not reveal blanks. */
  overscan?: number;
  /** Below this many rows the list renders in full. */
  threshold?: number;
};

export function VirtualList<T>({
  items,
  keyOf,
  renderItem,
  estimateHeight = 180,
  overscan = 4,
  threshold = 200,
}: VirtualListProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  /*
   * Measured heights live in state, not a ref: they are read while rendering to
   * decide the window, and a value a render depends on has to be one React
   * knows about.
   */
  const [heights, setHeights] = useState<ReadonlyMap<string, number>>(() => new Map());
  const [viewport, setViewport] = useState({ scrollY: 0, height: 0, top: 0 });

  const windowed = items.length > threshold;

  /*
   * The page itself scrolls — there is no inner scroll container — so the
   * measurements come from the window and the container's position within it.
   * That keeps the tab bar, the header and the safe-area insets working exactly
   * as they do on every other screen.
   */
  const measureViewport = useCallback(() => {
    const top = (containerRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY;
    setViewport({ scrollY: window.scrollY, height: window.innerHeight, top });
  }, []);

  useEffect(() => {
    if (!windowed) return;

    measureViewport();
    window.addEventListener('scroll', measureViewport, { passive: true });
    window.addEventListener('resize', measureViewport);

    return () => {
      window.removeEventListener('scroll', measureViewport);
      window.removeEventListener('resize', measureViewport);
    };
  }, [windowed, measureViewport]);

  const heightOf = (item: T): number => heights.get(keyOf(item)) ?? estimateHeight;

  const measure = useCallback((key: string, element: HTMLElement | null) => {
    if (element === null) return;

    const measured = element.getBoundingClientRect().height;
    if (measured === 0) return;

    setHeights((current) => {
      const known = current.get(key);
      // A pixel of tolerance: sub-pixel layout differences must not loop.
      if (known !== undefined && Math.abs(known - measured) < 1) return current;

      const next = new Map(current);
      next.set(key, measured);
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    if (windowed) measureViewport();
  }, [windowed, items.length, measureViewport]);

  if (!windowed) {
    return (
      <div ref={containerRef} className="flex flex-col gap-6">
        {items.map((item) => (
          <div key={keyOf(item)} ref={(element) => measure(keyOf(item), element)}>
            {renderItem(item)}
          </div>
        ))}
      </div>
    );
  }

  // Where the viewport sits relative to the top of the list.
  const scrolled = viewport.scrollY - viewport.top;
  const visibleFrom = scrolled - viewport.height;
  const visibleTo = scrolled + viewport.height * 2;

  let offset = 0;
  let startIndex = 0;
  let padTop = 0;
  const rendered: { item: T; key: string }[] = [];

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    if (item === undefined) continue;

    const height = heightOf(item);
    const bottom = offset + height;

    if (bottom >= visibleFrom && offset <= visibleTo) {
      if (rendered.length === 0) {
        startIndex = Math.max(0, index - overscan);
        padTop = offset;

        for (let back = index - 1; back >= startIndex; back -= 1) {
          const earlier = items[back];
          if (earlier === undefined) continue;
          padTop -= heightOf(earlier);
          rendered.unshift({ item: earlier, key: keyOf(earlier) });
        }
      }

      rendered.push({ item, key: keyOf(item) });
    }

    offset = bottom;
  }

  const lastIndex = startIndex + rendered.length;
  let padBottom = 0;
  for (let index = lastIndex + overscan; index < items.length; index += 1) {
    const item = items[index];
    if (item !== undefined) padBottom += heightOf(item);
  }

  for (let index = lastIndex; index < Math.min(lastIndex + overscan, items.length); index += 1) {
    const item = items[index];
    if (item !== undefined) rendered.push({ item, key: keyOf(item) });
  }

  return (
    <div ref={containerRef}>
      <div style={{ height: Math.max(0, padTop) }} aria-hidden="true" />
      <div className="flex flex-col gap-6">
        {rendered.map(({ item, key }) => (
          <div key={key} ref={(element) => measure(key, element)}>
            {renderItem(item)}
          </div>
        ))}
      </div>
      <div style={{ height: Math.max(0, padBottom) }} aria-hidden="true" />
    </div>
  );
}
